import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Reorder } from 'framer-motion';
import { Plus, Trash2, Loader2, ImagePlus, GripVertical, MapPin, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import MapPointPicker from '../MapPointPicker';
import { PriceInput } from './PriceInput';
import { resolveAssetUrl } from '../../lib/assets';
import { PLUGINS, SECTION_LABELS, pluginEnabled } from '../../data/businessCatalog';
import { normalizeDeliveryHours, DAY_LABELS } from '../../lib/deliveryHours';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Section = ({ title, action, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-white font-semibold" style={{ fontFamily: 'Outfit' }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);
export const Field = ({ label, children }) => (
  <div className="mb-4 last:mb-0">
    <label className="block text-white/60 text-xs font-semibold mb-1.5">{label}</label>
    {children}
  </div>
);
export const AddBtn = ({ onClick, label = 'Pievienot', testid }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 text-amber-400 text-sm font-semibold" data-testid={testid}>
    <Plus className="w-4 h-4" /> {label}
  </button>
);
export const DelBtn = ({ onClick }) => (
  <button onClick={onClick} className="col-span-1 text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
);
export const CoverField = ({ url, uploading, onUpload, testid }) => (
  <Field label="Vāka attēls">
    <div className="flex items-center gap-3">
      {url && <img src={resolveAssetUrl(url)} alt="" className="w-24 h-16 rounded-xl object-cover" />}
      <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/5 border border-white/10 text-white/70 text-sm cursor-pointer hover:bg-white/10">
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files[0])} data-testid={testid} />
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Augšupielādēt
      </label>
    </div>
  </Field>
);
export const GalleryField = ({ gallery, onAdd, onRemove }) => (
  <Section title="Galerija" action={
    <label className="inline-flex items-center gap-1 text-amber-400 text-sm font-semibold cursor-pointer">
      <input type="file" accept="image/*" className="hidden" onChange={(e) => onAdd(e.target.files[0])} data-testid="add-gallery" />
      <Plus className="w-4 h-4" /> Pievienot foto
    </label>
  }>
    <div className="grid grid-cols-4 gap-2">
      {(gallery || []).map((g, i) => (
        <div key={i} className="relative group">
          <img src={resolveAssetUrl(g)} alt="" className="w-full h-20 rounded-xl object-cover" />
          <button onClick={() => onRemove(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
    </div>
  </Section>
);
export const ContactInput = ({ icon: Icon, value, onChange, placeholder, testid }) => (
  <div className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3">
    <Icon className="w-4 h-4 text-white/40 shrink-0" />
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-10 bg-transparent text-white text-sm outline-none" data-testid={testid} />
  </div>
);
export const ModuleOrderEditor = ({ template, order, onChange }) => (
  <Section title="Sadaļu secība">
    <p className="text-white/40 text-sm mb-4">Velc sadaļas, lai mainītu to secību publiskajā lapā. Rezervāciju/kalendāra bloks vienmēr paliek augšā.</p>
    <Reorder.Group axis="y" values={order} onReorder={onChange} className="space-y-2" data-testid="module-order-list">
      {order.map((id) => (
        <Reorder.Item key={id} value={id}
          className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 cursor-grab active:cursor-grabbing select-none"
          data-testid={`module-order-item-${id}`}>
          <GripVertical className="w-4 h-4 text-white/40 shrink-0" />
          <span className="text-white text-sm font-medium">{SECTION_LABELS[id] || id}</span>
        </Reorder.Item>
      ))}
    </Reorder.Group>
  </Section>
);
export const PluginTogglesSection = ({ available, plugins, onToggle, template }) => (
  <Section title="Papildmoduļi">
    <p className="text-white/40 text-sm mb-4">Ieslēdz tikai tos moduļus, kurus vēlies rādīt savā lapā. Izslēgtie moduļi netiek rādīti apmeklētājiem.</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plugins-toggles">
      {available.map((pid) => {
        const p = PLUGINS[pid];
        if (!p) return null;
        const Icon = p.icon;
        const on = pluginEnabled(template, plugins, pid);
        return (
          <div key={pid} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`plugin-toggle-${pid}`}>
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-amber-400" /></div>
            <div className="min-w-0 flex-1"><p className="text-white text-sm font-semibold truncate">{p.label}</p><p className="text-white/40 text-xs truncate">{p.desc}</p></div>
            <Switch checked={on} onCheckedChange={() => onToggle(pid)} data-testid={`plugin-switch-${pid}`} />
          </div>
        );
      })}
    </div>
  </Section>
);

export const GiftCardsEditor = ({ data, onChange }) => {
  const cards = data?.gift_cards || [];
  const setCards = (a) => onChange({ gift_cards: a });
  return (
    <Section title="Dāvanu kartes" action={<AddBtn onClick={() => setCards([...cards, { name: 'Dāvanu karte', amount: 25 }])} testid="add-gift" />}>
      <p className="text-white/40 text-sm mb-4">Definē gatavas summas, ko klients var nopirkt (viņš var izvēlēties arī citu summu). Maksājums iet caur KoLV (Stripe) ar 10% platformas komisiju; kods tiek nosūtīts e-pastā.</p>
      <div className="space-y-3">
        {cards.length === 0 && <p className="text-white/40 text-sm">Vēl nav pievienota neviena karte. Klients joprojām var pirkt ar brīvu summu.</p>}
        {cards.map((g, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <Input value={g.name} onChange={(e) => { const a = [...cards]; a[i] = { ...g, name: e.target.value }; setCards(a); }}
              className="col-span-8 bg-white/5 border-white/10 text-white" data-testid={`giftcard-name-${i}`} />
            <PriceInput value={g.amount} emptyValue={0} decimals={2} onChange={(v) => { const a = [...cards]; a[i] = { ...g, amount: v }; setCards(a); }}
              className="col-span-3 bg-white/5 border-white/10 text-white" data-testid={`giftcard-amount-${i}`} />
            <DelBtn onClick={() => setCards(cards.filter((_, j) => j !== i))} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mt-3" data-testid="gift-partial-toggle">
        <div>
          <p className="text-white text-sm font-semibold">Atļaut daļēju izmantošanu</p>
          <p className="text-white/40 text-xs">Klients var izmantot karti pa daļām; tiek rādīts atlikums (nevis tikai “izmantota”).</p>
        </div>
        <Switch checked={!!data?.gift_card_partial_redeem} onCheckedChange={(v) => onChange({ gift_card_partial_redeem: v })} data-testid="gift-partial-switch" />
      </div>
    </Section>
  );
};

export const FaqEditor = ({ faq, onChange }) => (
  <Section title="BUJ (biežāk uzdotie jautājumi)" action={<AddBtn onClick={() => onChange([...(faq || []), { q: '', a: '' }])} testid="add-faq" />}>
    {(faq || []).length === 0 && <p className="text-white/40 text-sm">Pievieno jautājumus un atbildes.</p>}
    <div className="space-y-3">
      {(faq || []).map((f, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-3 space-y-2" data-testid={`faq-row-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={f.q} onChange={(e) => { const a = [...(faq || [])]; a[i] = { ...f, q: e.target.value }; onChange(a); }} placeholder="Jautājums" className="bg-white/5 border-white/10 text-white font-medium" data-testid={`faq-q-${i}`} />
            <DelBtn onClick={() => onChange((faq || []).filter((_, j) => j !== i))} />
          </div>
          <Textarea value={f.a || ''} onChange={(e) => { const a = [...(faq || [])]; a[i] = { ...f, a: e.target.value }; onChange(a); }} placeholder="Atbilde" rows={2} className="bg-white/5 border-white/10 text-white" data-testid={`faq-a-${i}`} />
        </div>
      ))}
    </div>
  </Section>
);
export const ReviewsEditor = ({ reviews, onChange }) => (
  <Section title="Atsauksmes" action={<AddBtn onClick={() => onChange([...(reviews || []), { author: '', rating: 5, text: '' }])} testid="add-review" />}>
    {(reviews || []).length === 0 && <p className="text-white/40 text-sm">Pievieno klientu atsauksmes.</p>}
    <div className="space-y-3">
      {(reviews || []).map((r, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-3 space-y-2" data-testid={`review-row-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={r.author} onChange={(e) => { const a = [...(reviews || [])]; a[i] = { ...r, author: e.target.value }; onChange(a); }} placeholder="Klienta vārds" className="bg-white/5 border-white/10 text-white font-medium" data-testid={`review-author-${i}`} />
            <select value={r.rating || 5} onChange={(e) => { const a = [...(reviews || [])]; a[i] = { ...r, rating: parseInt(e.target.value, 10) }; onChange(a); }}
              className="h-10 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2 shrink-0" data-testid={`review-rating-${i}`}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n} className="bg-[#0A0A0A]">{n} ★</option>)}
            </select>
            <DelBtn onClick={() => onChange((reviews || []).filter((_, j) => j !== i))} />
          </div>
          <Textarea value={r.text || ''} onChange={(e) => { const a = [...(reviews || [])]; a[i] = { ...r, text: e.target.value }; onChange(a); }} placeholder="Atsauksmes teksts" rows={2} className="bg-white/5 border-white/10 text-white" data-testid={`review-text-${i}`} />
        </div>
      ))}
    </div>
  </Section>
);

// Small inline image upload (per service / menu item row) with optional remove.
export const ItemImageField = ({ url, onUpload, onRemove, testid }) => (
  <div className="relative shrink-0">
    <label className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex items-center justify-center cursor-pointer border border-white/10" title="Attēls (neobligāti)">
      {url ? <img src={resolveAssetUrl(url)} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-4 h-4 text-white/40" />}
      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} data-testid={testid} />
    </label>
    {url && onRemove && (
      <button type="button" onClick={onRemove} aria-label="Noņemt attēlu"
        data-testid={testid ? `${testid}-remove` : undefined}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 text-white flex items-center justify-center border border-white/20 hover:bg-red-600 transition-colors">
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
);

// Multi-image field (up to `max`) — cover + thumbnails, each removable.
export const ItemImagesField = ({ images = [], onAddFile, onRemoveAt, uploading, testid, max = 5 }) => (
  <div className="flex items-center gap-1.5 flex-wrap shrink-0" data-testid={testid ? `${testid}-wrap` : undefined}>
    {images.map((u, i) => (
      <div key={i} className="relative shrink-0">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 border border-white/10">
          <img src={resolveAssetUrl(u)} alt="" className="w-full h-full object-cover" />
        </div>
        <button type="button" onClick={() => onRemoveAt(i)} aria-label="Noņemt attēlu"
          data-testid={testid ? `${testid}-remove-${i}` : undefined}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 text-white flex items-center justify-center border border-white/20 hover:bg-red-600 transition-colors">
          <X className="w-3 h-3" />
        </button>
        {i === 0 && images.length > 1 && (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold px-1 rounded bg-[#D97736] text-black leading-tight">Vāks</span>
        )}
      </div>
    ))}
    {images.length < max && (
      <label className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center cursor-pointer border border-dashed border-white/20 hover:border-white/40 transition-colors"
        title={`Pievienot attēlu (${images.length}/${max})`} data-testid={testid ? `${testid}-add` : undefined}>
        {uploading ? <Loader2 className="w-4 h-4 text-white/40 animate-spin" /> : <ImagePlus className="w-4 h-4 text-white/40" />}
        <input type="file" accept="image/*" className="hidden" data-testid={testid}
          onChange={(e) => { if (e.target.files[0]) { onAddFile(e.target.files[0]); e.target.value = ''; } }} />
      </label>
    )}
  </div>
);

// Set an item's photo array, keeping legacy `image_url` in sync with the cover.
export const withImages = (item, imgs) => ({ ...item, images: imgs, image_url: imgs[0] || null });

export const WorkingHoursField = ({ value, onChange, testid = 'hours-field' }) => (
  <Section title="Darba laiks">
    <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder={"P.–Pk. 9:00–18:00\nSestdien 10:00–15:00\nSvētdien slēgts"} rows={3}
      className="bg-white/5 border-white/10 text-white" data-testid={testid} />
  </Section>
);

export const DeliveryHoursEditor = ({ value, onChange, title = 'Piegādes laiks', toggleLabel = 'Ierobežot piegādi pēc laika', toggleDesc = 'Ārpus šī laika piegādes pasūtījumus nevar veikt. Pašizvešana / ēšana uz vietas strādā vienmēr.', tid = 'delivery' }) => {
  const dh = normalizeDeliveryHours(value);
  const setDay = (i, patch) => onChange({ ...dh, days: dh.days.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  return (
    <Section title={title}>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-3" data-testid={`${tid}-hours-toggle`}>
        <div>
          <p className="text-white text-sm font-semibold">{toggleLabel}</p>
          <p className="text-white/40 text-xs">{toggleDesc}</p>
        </div>
        <Switch checked={dh.enabled} onCheckedChange={(v) => onChange({ ...dh, enabled: v })} data-testid={`${tid}-hours-switch`} />
      </div>
      {dh.enabled && (
        <div className="space-y-2" data-testid={`${tid}-hours-days`}>
          {dh.days.map((d, i) => (
            <div key={i} className="flex items-center gap-3 flex-wrap">
              <span className="w-24 shrink-0 text-sm text-white/70">{DAY_LABELS[i]}</span>
              <Switch checked={!!d.open} onCheckedChange={(v) => setDay(i, { open: v })} data-testid={`${tid}-day-open-${i}`} />
              {d.open ? (
                <div className="flex items-center gap-2">
                  <input type="time" value={d.from || ''} onChange={(e) => setDay(i, { from: e.target.value })}
                    className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white [color-scheme:dark]" data-testid={`${tid}-from-${i}`} />
                  <span className="text-white/40 text-sm">–</span>
                  <input type="time" value={d.to || ''} onChange={(e) => setDay(i, { to: e.target.value })}
                    className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white [color-scheme:dark]" data-testid={`${tid}-to-${i}`} />
                </div>
              ) : <span className="text-white/40 text-sm">Slēgts</span>}
            </div>
          ))}
          <p className="text-white/40 text-xs">Laiks tiek rēķināts pēc Latvijas laika (Europe/Riga).</p>
        </div>
      )}
    </Section>
  );
};

export const PromotionsEditor = ({ promotions, onChange }) => (
  <Section title="Akcijas & piedāvājumi" action={<AddBtn onClick={() => onChange([...(promotions || []), { title: '', description: '' }])} testid="add-promo" />}>
    {(promotions || []).length === 0 && <p className="text-white/40 text-sm">Pievieno īpašos piedāvājumus vai atlaides.</p>}
    <div className="space-y-3">
      {(promotions || []).map((p, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`promo-row-${i}`}>
          <Input value={p.title} onChange={(e) => { const a = [...(promotions || [])]; a[i] = { ...p, title: e.target.value }; onChange(a); }}
            placeholder="Virsraksts" className="col-span-4 bg-white/5 border-white/10 text-white" />
          <Input value={p.description || ''} onChange={(e) => { const a = [...(promotions || [])]; a[i] = { ...p, description: e.target.value }; onChange(a); }}
            placeholder="Apraksts" className="col-span-7 bg-white/5 border-white/10 text-white" />
          <DelBtn onClick={() => onChange((promotions || []).filter((_, j) => j !== i))} />
        </div>
      ))}
    </div>
  </Section>
);

export const LocationEditor = ({ address, lat, lng, onPatch }) => {
  const [geocoding, setGeocoding] = React.useState(false);
  const geocode = async () => {
    if (!address) { toast.error('Vispirms ievadi adresi'); return; }
    setGeocoding(true);
    try {
      let res;
      try {
        res = await axios.get(`${API}/geocode`, { params: { q: address } });
      } catch (e1) {
        const stripped = address.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
        if (stripped && stripped !== address) { res = await axios.get(`${API}/geocode`, { params: { q: stripped } }); }
        else { throw e1; }
      }
      onPatch({ lat: res.data.lat, lng: res.data.lng });
      toast.success('Atrasts kartē! Vari precizēt punktu, velkot to.');
    } catch { toast.error('Adrese nav atrasta — pārvieto punktu kartē manuāli'); }
    finally { setGeocoding(false); }
  };
  return (
    <Section title="Atrašanās vieta & karte">
      <Field label="Adrese">
        <Input value={address || ''} onChange={(e) => onPatch({ address: e.target.value })}
          placeholder="Piem. Brīvības iela 1, Rīga" className="bg-white/5 border-white/10 text-white" data-testid="loc-address" />
      </Field>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-white/60 text-xs font-semibold">Atrašanās vieta kartē</label>
        <button type="button" onClick={geocode} disabled={geocoding}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 disabled:opacity-50" data-testid="loc-geocode-btn">
          {geocoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />} Atrast adresi kartē
        </button>
      </div>
      <MapPointPicker lat={lat} lng={lng} onChange={(la, ln) => onPatch({ lat: la, lng: ln })} />
      <p className="text-white/40 text-[11px] mt-1.5">Uzspied "Atrast adresi kartē" vai velc punktu, lai precīzi norādītu atrašanās vietu.</p>
    </Section>
  );
};
